import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Plus, Search, TrendingUp, TrendingDown, Building2, Users as UsersIcon, FileText, Plug, MoreVertical, Trash2, Eye, RefreshCw, Upload, X, Image as ImageIcon, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { useCompaniesList } from '@/hooks/useCompaniesData';
import { useCompany } from '@/contexts/CompanyContext';
import { useCurrenciesList } from '@/hooks/useCurrenciesData';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import type { CompanyApi, CreateCompanyRequest, UpdateCompanyRequest, CurrencyApi } from '@/types/api/companies';
import { FINANCIALS_PRODUCT_ID, DIMENSIONS_VNEXT_PRODUCT_ID } from '@/types/api/companies';
import { HelpSupportButton } from '@/components/help-support';
import { cn } from '@/lib/utils';
import { useMode } from '@/providers/ModeProvider';

interface Company {
  id: string;
  name: string;
  status: 'active' | 'inactive' | 'pending_setup';
  logo?: string;
  description?: string;
  integrationStatus: 'connected' | 'partial' | 'none';
  userCount: number;
  createdAt: string;
  linkedEvoSubscriptionId?: string;
  linkedSubscriptionName?: string;
  linkedProductName?: string;
  linkedProductId?: string;
  currencyId?: number;
  code?: string;
  taxId?: string;
  linkedSubLedger?: string;
  externalCompanyName?: string;
  address?: string;
  vatRegistrationNumber?: string;
  companyRegistrationNumber?: string;
  phone?: string;
  email?: string;
}

interface Instance {
  instanceId: string;
  name: string;
  organisationId: string;
  organisationName: string;
}

interface BankDetail {
  id: string;
  currency: string;
  sortCode: string;
  accountNumber: string;
  accountName: string;
  ibanNumber: string;
  swiftBicCode: string;
}

function getProductId(product: string): string | undefined {
  if (product === 'Financials') return FINANCIALS_PRODUCT_ID;
  if (product === 'DimensionsvNext') return DIMENSIONS_VNEXT_PRODUCT_ID;
  return undefined;
}

function resetInstanceIdIfMissing(instances: Instance[]) {
  return (currentId: string) => {
    if (currentId && !instances.some(x => x.instanceId === currentId)) {
      return '';
    }
    return currentId;
  };
}

function getIntegrationBadgeVariant(status: string | undefined): 'default' | 'secondary' | 'outline' {
  if (status === 'connected') return 'default';
  if (status === 'partial') return 'secondary';
  return 'outline';
}

const DEMO_INSTANCES: Instance[] = [
  { instanceId: 'demo-instance-1', name: 'Demo Instance 1', organisationId: 'demo-org-1', organisationName: 'Demo Organisation 1' },
  { instanceId: 'demo-instance-2', name: 'Demo Instance 2', organisationId: 'demo-org-2', organisationName: 'Demo Organisation 2' },
];

interface SubLedgerOption {
  code: string;
  name: string;
}

const SUBLEDGER_NONE_VALUE = '__none__';

const DEMO_SUB_LEDGERS: SubLedgerOption[] = [
  { code: 'SL1', name: 'Sub Ledger 1' },
  { code: 'SL2', name: 'Sub Ledger 2' },
  { code: 'SL3', name: 'Sub Ledger 3' },
  { code: 'SL4', name: 'Sub Ledger 4' },
  { code: 'SL5', name: 'Sub Ledger 5' },
];

interface FetchInstancesCallbacks {
  setInstances: (instances: Instance[]) => void;
  setInstanceId: (updater: (currentId: string) => string) => void;
  setLoading: (loading: boolean) => void;
  setError?: (error: string | null) => void;
}

function fetchInstancesForProduct(
  product: string,
  isDemo: boolean,
  apiGet: <T>(url: string) => Promise<T>,
  callbacks: FetchInstancesCallbacks,
) {
  if (product !== 'Financials' && product !== 'DimensionsvNext') {
    callbacks.setInstances([]);
    callbacks.setInstanceId(() => '');
    return;
  }

  callbacks.setLoading(true);
  callbacks.setError?.(null);
  const productId = getProductId(product);

  if (isDemo) {
    setTimeout(() => {
      callbacks.setInstances(DEMO_INSTANCES);
      callbacks.setInstanceId(resetInstanceIdIfMissing(DEMO_INSTANCES));
      callbacks.setLoading(false);
    }, 300);
    return;
  }

  apiGet<Instance[]>(`/api/v1/externalInstances?productId=${productId}`)
    .then((data) => {
      callbacks.setInstances(data);
      callbacks.setInstanceId(resetInstanceIdIfMissing(data));
      callbacks.setLoading(false);
    })
    .catch((error) => {
      console.error('Error fetching instances:', error);
      callbacks.setError?.('Failed to load instances. Please try again.');
      callbacks.setLoading(false);
    });
}

interface FetchSubLedgersCallbacks {
  setSubLedgers: (subLedgers: SubLedgerOption[]) => void;
  setLoading: (loading: boolean) => void;
  setError?: (error: string | null) => void;
}

function fetchSubLedgersForInstance(
  instanceId: string,
  product: string,
  isDemo: boolean,
  apiGet: <T>(url: string) => Promise<T>,
  callbacks: FetchSubLedgersCallbacks,
) {
  if (!instanceId || !product) {
    callbacks.setSubLedgers([]);
    return;
  }

  callbacks.setLoading(true);
  callbacks.setError?.(null);

  if (isDemo) {
    setTimeout(() => {
      callbacks.setSubLedgers(DEMO_SUB_LEDGERS);
      callbacks.setLoading(false);
    }, 300);
    return;
  }

  const productId = getProductId(product);
  if (!productId) {
    callbacks.setSubLedgers([]);
    return;
  }

  apiGet<SubLedgerOption[]>(`/api/v1/externalInstances/${instanceId}/subcompanies?productId=${productId}`)
    .then((data) => {
      callbacks.setSubLedgers(data);
      callbacks.setLoading(false);
    })
    .catch((error) => {
      console.error('Error fetching sub ledgers:', error);
      callbacks.setError?.('Failed to load sub ledgers. Please try again.');
      callbacks.setLoading(false);
    });
}

function sortCompanies(a: Company, b: Company, sortOption: string): number {
  switch (sortOption) {
    case 'name-asc':
      return a.name.localeCompare(b.name);
    case 'name-desc':
      return b.name.localeCompare(a.name);
    case 'newest':
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    case 'oldest':
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    default:
      return 0;
  }
}

const INITIAL_FORM_DATA = {
  companyId: '', name: '', address: '', vatRegistrationNo: '', companyRegistrationNo: '',
  phone: '', email: '', status: 'active' as 'active' | 'inactive', logo: '', description: '',
  currencyId: undefined as number | undefined, code: '', taxId: '',
};

const INITIAL_BANK_DETAIL: BankDetail = {
  id: '1', currency: '', sortCode: '', accountNumber: '', accountName: '', ibanNumber: '', swiftBicCode: '',
};

function validateLogoFile(file: File): string | null {
  const acceptedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/svg+xml'];
  if (!acceptedTypes.includes(file.type)) return 'Please upload an image file (PNG, JPG, GIF, or SVG)';
  if (file.size > 5 * 1024 * 1024) return 'File size must be less than 5MB';
  return null;
}

async function editCompanyAction(
  selectedCompany: Company,
  formData: { name: string; status: string; description: string },
  isDemo: boolean,
  apiPut: (url: string, body: unknown) => Promise<unknown>,
  refetchCompanies: () => Promise<unknown>,
  updateLocal: (updater: (prev: Company[]) => Company[]) => void,
  onDone: () => void,
): Promise<void> {
  try {
    if (isDemo) {
      await new Promise(resolve => setTimeout(resolve, 300));
      updateLocal(prev => prev.map(c =>
        c.id === selectedCompany.id
          ? { ...c, name: formData.name, status: formData.status as 'active' | 'inactive', description: formData.description }
          : c
      ));
    } else {
      await apiPut(`/api/v1/company/${selectedCompany.id}`, {
        name: formData.name,
        code: selectedCompany.code,
        currencyId: selectedCompany.currencyId,
        taxId: selectedCompany.taxId,
        externalCompanyCode: selectedCompany.linkedSubLedger ?? null,
        externalCompanyName: selectedCompany.externalCompanyName ?? null,
        status: formData.status,
      } satisfies UpdateCompanyRequest);
      await refetchCompanies();
    }
    toast.success('Company updated successfully');
    onDone();
  } catch (error) {
    console.error('Error updating company:', error);
    toast.error('Failed to update company');
  }
}

async function deleteCompanyAction(
  selectedCompany: Company,
  isDemo: boolean,
  apiDelete: (url: string) => Promise<unknown>,
  refetchCompanies: () => Promise<unknown>,
  updateLocal: (updater: (prev: Company[]) => Company[]) => void,
  onDone: () => void,
): Promise<void> {
  try {
    if (isDemo) {
      await new Promise(resolve => setTimeout(resolve, 300));
      updateLocal(prev => prev.filter(c => c.id !== selectedCompany.id));
    } else {
      await apiDelete(`/api/v1/company/${selectedCompany.id}`);
      await refetchCompanies();
    }
    toast.success('Company deleted successfully');
    onDone();
  } catch (error) {
    console.error('Error deleting company:', error);
    toast.error('Failed to delete company');
  }
}

interface SaveIntegrationParams {
  selectedCompany: Company;
  selectedInstanceId: string;
  selectedProduct: string;
  selectedSubLedger: string;
  subLedgers: SubLedgerOption[];
  instances: Instance[];
  isDemo: boolean;
  apiPut: (url: string, body: unknown) => Promise<unknown>;
  refetchCompanies: () => Promise<unknown>;
  updateLocal: (updater: (prev: Company[]) => Company[]) => void;
  onDone: () => void;
}

/**
 * Saves integration details for an existing company.
 * Integration linking (subscription/product) is set at creation time and is immutable.
 * This function only updates the sub-ledger assignment (ExternalCompanyCode/ExternalCompanyName)
 * on existing companies.
 */
async function saveIntegrationAction(params: SaveIntegrationParams): Promise<void> {
  const { selectedCompany, selectedInstanceId, selectedProduct, selectedSubLedger,
    subLedgers, instances, isDemo, apiPut, refetchCompanies, updateLocal, onDone } = params;
  const selectedInstance = instances.find(x => x.instanceId === selectedInstanceId);
  const linkedDatabaseName = selectedInstance?.name || undefined;
  const productId = getProductId(selectedProduct);
  const hasSubLedger = selectedSubLedger !== '' && selectedSubLedger !== SUBLEDGER_NONE_VALUE;
  try {
    if (isDemo) {
      await new Promise(resolve => setTimeout(resolve, 300));
      updateLocal(prev => prev.map(x =>
        x.id === selectedCompany.id
          ? {
              ...x,
              linkedEvoSubscriptionId: selectedInstanceId || undefined,
              linkedSubscriptionName: linkedDatabaseName || undefined,
              linkedProductId: selectedInstanceId ? String(productId) : undefined,
              linkedProductName: selectedInstanceId ? selectedProduct : undefined,
              linkedSubLedger: hasSubLedger ? selectedSubLedger : undefined,
              integrationStatus: selectedInstanceId ? 'connected' as const : 'none' as const,
            }
          : x
      ));
    } else {
      await apiPut(`/api/v1/company/${selectedCompany.id}`, {
        name: selectedCompany.name,
        code: selectedCompany.code,
        currencyId: selectedCompany.currencyId,
        taxId: selectedCompany.taxId,
        externalCompanyCode: hasSubLedger ? selectedSubLedger : null,
        externalCompanyName: hasSubLedger ? (subLedgers.find(x => x.code === selectedSubLedger)?.name || selectedSubLedger) : null,
        status: selectedCompany.status,
      });
      await refetchCompanies();
    }
    toast.success('Company integration saved successfully');
    onDone();
  } catch (error) {
    console.error('Error saving company integration:', error);
    toast.error('Failed to save company integration');
    onDone();
  }
}


async function syncCompanyAction(
  company: Company,
  isDemo: boolean,
  apiPost: (url: string, body: unknown) => Promise<unknown>,
): Promise<void> {
  try {
    if (isDemo) {
      await new Promise(resolve => setTimeout(resolve, 300));
    } else {
      await apiPost('/api/v1/sync/all', {});
    }
    toast.success(`Sync triggered for ${company.name}`);
  } catch (error) {
    console.error('Error triggering sync:', error);
    toast.error('Failed to trigger sync');
  }
}


interface EditViewFormData {
  companyId: string;
  companyName: string;
  status: 'active' | 'inactive' | 'pending_setup';
  taxId: string;
  description: string;
  address: string;
  vatRegistrationNumber: string;
  companyRegistrationNumber: string;
  phone: string;
  email: string;
  logo: string | null;
}

function buildEditFormFromCompany(company: Company): EditViewFormData {
  return {
    companyId: company.code || '',
    companyName: company.name,
    status: company.status,
    taxId: company.taxId || '',
    description: company.description || '',
    address: company.address || '',
    vatRegistrationNumber: company.vatRegistrationNumber || '',
    companyRegistrationNumber: company.companyRegistrationNumber || '',
    phone: company.phone || '',
    email: company.email || '',
    logo: null,
  };
}

function TrendIndicator({ trend }: Readonly<{ trend: number }>) {
  if (trend > 0) {
    return (
      <>
        <TrendingUp className="h-3 w-3 text-green-500 mr-1" />
        <span className="text-green-500">+{trend}%</span>
      </>
    );
  }
  return (
    <>
      <TrendingDown className="h-3 w-3 text-red-500 mr-1" />
      <span className="text-red-500">{trend}%</span>
    </>
  );
}

function IntegrationStatusBadge({ status, onManageClick, company }: Readonly<{ status: Company['integrationStatus']; onManageClick: (e: React.MouseEvent) => void; company?: Company }>) {
  // Check if company is in "Pending Setup" state (has linked instance but not connected)
  const isPendingSetup = status === 'none' && company?.linkedEvoSubscriptionId;
  
  switch (status) {
    case 'connected':
      return (
        <Badge 
          className="bg-green-500/10 text-green-500 hover:bg-green-500/20 cursor-pointer transition-colors"
          onClick={onManageClick}
        >
          <Plug className="h-3 w-3 mr-1" />Connected
        </Badge>
      );
    case 'partial':
      return (
        <Badge 
          className="bg-yellow-500/10 text-yellow-500 hover:bg-yellow-500/20 cursor-pointer transition-colors"
          onClick={onManageClick}
        >
          <Plug className="h-3 w-3 mr-1" />Partial
        </Badge>
      );
    case 'none':
      if (isPendingSetup) {
        return (
          <Badge 
            className="bg-orange-500/10 text-orange-500 hover:bg-orange-500/20 cursor-pointer transition-colors"
            onClick={onManageClick}
          >
            <Plug className="h-3 w-3 mr-1" />Pending Setup
          </Badge>
        );
      }
      return (
        <Badge 
          variant="outline" 
          className="cursor-pointer hover:bg-accent transition-colors"
          onClick={onManageClick}
        >
          <Plug className="h-3 w-3 mr-1" />Not Connected
        </Badge>
      );
  }
}


interface DetailsTabContentProps {
  company: Company | null;
  isEditing: boolean;
  formData: EditViewFormData;
  onFormDataChange: (data: EditViewFormData) => void;
  draggingLogo: boolean;
  onLogoDragOver: (e: React.DragEvent) => void;
  onLogoDragLeave: (e: React.DragEvent) => void;
  onLogoDrop: (e: React.DragEvent) => void;
  onLogoFile: (file: File) => void;
  currencies: CurrencyApi[];
}

function DetailsTabContent({ company, isEditing, formData, onFormDataChange, draggingLogo, onLogoDragOver, onLogoDragLeave, onLogoDrop, onLogoFile, currencies }: Readonly<DetailsTabContentProps>) {
  const renderEditableField = (label: string, field: 'taxId' | 'description' | 'address' | 'vatRegistrationNumber' | 'companyRegistrationNumber' | 'phone' | 'email', placeholder: string, colSpan?: boolean, type?: string) => {
    const fieldValue = formData[field];
    const companyValue = company?.[field as keyof Company] as string | undefined;
    return (
      <div className={colSpan ? "space-y-2 col-span-2" : "space-y-2"}>
        <Label className="text-muted-foreground">{label}</Label>
        {isEditing ? (
          <Input
            type={type || 'text'}
            value={fieldValue}
            onChange={(e) => onFormDataChange({ ...formData, [field]: e.target.value })}
            placeholder={placeholder}
          />
        ) : (
          <p className="text-sm font-medium">{companyValue || '-'}</p>
        )}
      </div>
    );
  };

  const renderLogoSection = () => {
    if (isEditing) {
      return (
        <div className="space-y-2">
          <button
            type="button"
            onDragOver={onLogoDragOver}
            onDragLeave={onLogoDragLeave}
            onDrop={onLogoDrop}
            className={cn(
              "w-full border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
              draggingLogo ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50"
            )}
            onClick={() => document.getElementById('logo-upload-view')?.click()}
          >
            <input
              id="logo-upload-view"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  onLogoFile(file);
                }
              }}
            />
            <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Drag and drop your logo here, or click to browse
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Supports: JPG, PNG, GIF
            </p>
          </button>
          {(formData.logo || company?.logo) && (
            <div className="flex items-center gap-4 p-4 border rounded-lg">
              <img 
                src={formData.logo || company?.logo || ''} 
                alt="Company Logo" 
                className="h-16 w-16 object-contain border rounded"
              />
              <div className="flex-1">
                <p className="text-sm font-medium">Current logo</p>
                <p className="text-xs text-muted-foreground">Click or drag to replace</p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onFormDataChange({ ...formData, logo: null });
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      );
    }
    if (company?.logo) {
      return (
        <div className="flex items-center gap-4">
          <img 
            src={company.logo} 
            alt="Company Logo" 
            className="h-16 w-16 object-contain border rounded"
          />
          <p className="text-sm text-muted-foreground">Logo uploaded</p>
        </div>
      );
    }
    return <p className="text-sm font-medium">-</p>;
  };

  return (
    <TabsContent value="details" className="space-y-6 mt-6 flex-1 overflow-y-auto">
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Company Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-muted-foreground">Company ID</Label>
            {isEditing && company?.status === 'pending_setup' ? (
              <Input
                value={formData.companyId}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase().slice(0, 7);
                  if (/^[A-Z0-9]*$/.test(value)) {
                    onFormDataChange({ ...formData, companyId: value });
                  }
                }}
                placeholder="Enter company ID (max 7 alphanumeric)"
                maxLength={7}
              />
            ) : (
              <p className="text-sm font-medium">{company?.code || '-'}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-muted-foreground">Status</Label>
            {isEditing ? (
              <div className="flex items-center space-x-2">
                <Switch
                  id="status-toggle"
                  checked={formData.status === 'active'}
                  onCheckedChange={(checked) =>
                    onFormDataChange({ ...formData, status: checked ? 'active' : 'inactive' })
                  }
                />
                <Label htmlFor="status-toggle" className="cursor-pointer">
                  {formData.status === 'active' && 'Active'}
                  {formData.status === 'pending_setup' && 'Pending Setup'}
                  {formData.status !== 'active' && formData.status !== 'pending_setup' && 'Inactive'}
                </Label>
              </div>
            ) : (
              <div>
                {company?.status === 'active' && (
                  <Badge variant="default">Active</Badge>
                )}
                {company?.status === 'pending_setup' && (
                  <Badge className="bg-orange-500/10 text-orange-500 hover:bg-orange-500/20">
                    Pending Setup
                  </Badge>
                )}
                {company?.status !== 'active' && company?.status !== 'pending_setup' && (
                  <Badge variant="secondary">Inactive</Badge>
                )}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-muted-foreground">Company Name</Label>
            {isEditing ? (
              <Input
                value={formData.companyName}
                onChange={(e) =>
                  onFormDataChange({ ...formData, companyName: e.target.value })
                }
                placeholder="Enter company name"
              />
            ) : (
              <p className="text-sm font-medium">{company?.name || '-'}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-muted-foreground">Currency</Label>
            <p className="text-sm font-medium">
              {company?.currencyId 
                ? currencies.find(c => c.id === company.currencyId)?.code || '-'
                : '-'
              }
            </p>
          </div>
          <div className="space-y-2 col-span-2">
            <Label className="text-muted-foreground">Address</Label>
            {isEditing ? (
              <Textarea
                value={formData.address}
                onChange={(e) =>
                  onFormDataChange({ ...formData, address: e.target.value })
                }
                placeholder="Enter address"
                rows={3}
              />
            ) : (
              <p className="text-sm font-medium">{company?.address || '-'}</p>
            )}
          </div>
          {renderEditableField('VAT Registration Number', 'vatRegistrationNumber', 'Enter VAT registration number')}
          {renderEditableField('Company Registration Number', 'companyRegistrationNumber', 'Enter company registration number')}
          {renderEditableField('Phone', 'phone', 'Enter phone number')}
          {renderEditableField('Email', 'email', 'Enter email address', false, 'email')}
          <div className="space-y-2 col-span-2">
            <Label className="text-muted-foreground">Company Logo</Label>
            {renderLogoSection()}
          </div>
        </div>
      </div>

      <div className="space-y-4 pt-4 border-t">
        <h3 className="text-lg font-semibold">Additional Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-muted-foreground">User Count</Label>
            <p className="text-sm font-medium">{company?.userCount || 0}</p>
          </div>
          <div className="space-y-2">
            <Label className="text-muted-foreground">Created At</Label>
            <p className="text-sm font-medium">
              {company?.createdAt 
                ? new Date(company.createdAt).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  })
                : '-'
              }
            </p>
          </div>
        </div>
      </div>
    </TabsContent>
  );
}


interface BankDetailsTabContentProps {
  isEditing: boolean;
  bankDetails: BankDetail[];
  onAddRow: () => void;
  onRemoveRow: (id: string) => void;
  onChange: (id: string, field: keyof BankDetail, value: string) => void;
}

function BankDetailsTabContent({ isEditing, bankDetails, onAddRow, onRemoveRow, onChange }: Readonly<BankDetailsTabContentProps>) {
  const renderCell = (detail: BankDetail, field: keyof BankDetail, placeholder: string, minWidth: string) => {
    if (isEditing) {
      return (
        <Input
          value={detail[field]}
          onChange={(e) => onChange(detail.id, field, e.target.value)}
          placeholder={placeholder}
          className={minWidth}
        />
      );
    }
    return <span>{detail[field] || '-'}</span>;
  };

  return (
    <TabsContent value="bank-details" className="space-y-4 mt-6 flex-1 overflow-y-auto">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Bank Account Details</h3>
          {isEditing && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onAddRow}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Bank Detail Row
            </Button>
          )}
        </div>
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Currency</TableHead>
                <TableHead>Sort Code</TableHead>
                <TableHead>Account Number</TableHead>
                <TableHead>Account Name</TableHead>
                <TableHead>IBAN Number</TableHead>
                <TableHead>SWIFT/BIC Code</TableHead>
                {isEditing && <TableHead className="w-[50px]"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {bankDetails.length > 0 ? (
                bankDetails.map((detail) => (
                  <TableRow key={detail.id}>
                    <TableCell>{renderCell(detail, 'currency', 'USD', 'min-w-[80px]')}</TableCell>
                    <TableCell>{renderCell(detail, 'sortCode', '00-00-00', 'min-w-[100px]')}</TableCell>
                    <TableCell>{renderCell(detail, 'accountNumber', '12345678', 'min-w-[120px]')}</TableCell>
                    <TableCell>{renderCell(detail, 'accountName', 'Account Name', 'min-w-[150px]')}</TableCell>
                    <TableCell>{renderCell(detail, 'ibanNumber', 'GB00XXXX00000000000000', 'min-w-[200px]')}</TableCell>
                    <TableCell>{renderCell(detail, 'swiftBicCode', 'XXXXGB00', 'min-w-[120px]')}</TableCell>
                    {isEditing && (
                      <TableCell>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => onRemoveRow(detail.id)}
                          disabled={bankDetails.length === 1}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={isEditing ? 7 : 6} className="text-center text-muted-foreground py-8">
                    No bank details available for this company
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {!isEditing && (
          <p className="text-sm text-muted-foreground">
            Bank details can be managed through the edit function.
          </p>
        )}
      </div>
    </TabsContent>
  );
}

interface CompanyViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: Company | null;
  isDemo: boolean;
  apiGet: <T>(url: string) => Promise<T>;
  apiPut: (url: string, body: unknown) => Promise<unknown>;
  refetchCompanies: () => Promise<unknown>;
  onSave: (companyId: string, updates: Partial<Company>) => void;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function CompanyViewDialog({ open, onOpenChange, company, isDemo, apiGet: _apiGet, apiPut, refetchCompanies, onSave }: Readonly<CompanyViewDialogProps>) {
  const { data: currencies = [] } = useCurrenciesList();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<EditViewFormData>({
    companyId: '', companyName: '', status: 'active', taxId: '', description: '',
    address: '', vatRegistrationNumber: '', companyRegistrationNumber: '',
    phone: '', email: '', logo: null,
  });
  const [localBankDetails, setLocalBankDetails] = useState<BankDetail[]>([INITIAL_BANK_DETAIL]);
  const [draggingLogo, setDraggingLogo] = useState(false);

  useEffect(() => {
    if (company && open) {
      setFormData(buildEditFormFromCompany(company));
      setLocalBankDetails([INITIAL_BANK_DETAIL]);
      setIsEditing(false);
    }
  }, [company, open]);

  const handleLogoFile = (file: File) => {
    if (file?.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, logo: reader.result as string }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoDragOver = (e: React.DragEvent) => { e.preventDefault(); setDraggingLogo(true); };
  const handleLogoDragLeave = (e: React.DragEvent) => { e.preventDefault(); setDraggingLogo(false); };
  const handleLogoDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDraggingLogo(false);
    const file = e.dataTransfer.files[0];
    if (file) handleLogoFile(file);
  };

  const handleAddBankRow = () => {
    const newId = (localBankDetails.length + 1).toString();
    setLocalBankDetails([...localBankDetails, {
      id: newId, currency: '', sortCode: '', accountNumber: '',
      accountName: '', ibanNumber: '', swiftBicCode: '',
    }]);
  };

  const handleRemoveBankRow = (id: string) => {
    if (localBankDetails.length > 1) {
      setLocalBankDetails(localBankDetails.filter(d => d.id !== id));
    }
  };

  const handleBankChange = (id: string, field: keyof BankDetail, value: string) => {
    setLocalBankDetails(localBankDetails.map(d => d.id === id ? { ...d, [field]: value } : d));
  };

  const handleSave = async () => {
    if (!company) return;

    try {
      if (!isDemo) {
        await apiPut(`/api/v1/company/${company.id}`, {
          name: formData.companyName,
          code: formData.companyId,
          currencyId: company.currencyId,
          taxId: formData.taxId,
          externalCompanyCode: company.linkedSubLedger ?? null,
          externalCompanyName: company.externalCompanyName ?? null,
          status: formData.status,
        } satisfies UpdateCompanyRequest);
        await refetchCompanies();
      }

      onSave(company.id, {
        code: formData.companyId,
        name: formData.companyName,
        status: formData.status,
        taxId: formData.taxId,
        description: formData.description,
        address: formData.address,
        vatRegistrationNumber: formData.vatRegistrationNumber,
        companyRegistrationNumber: formData.companyRegistrationNumber,
        phone: formData.phone,
        email: formData.email,
        logo: formData.logo || company.logo,
        linkedEvoSubscriptionId: company.linkedEvoSubscriptionId,
        linkedSubscriptionName: company.linkedSubscriptionName,
        linkedProductId: company.linkedProductId,
        linkedProductName: company.linkedProductName,
        linkedSubLedger: company.linkedSubLedger,
        integrationStatus: company.integrationStatus,
      });
      setIsEditing(false);
      toast.success("Company details updated successfully");
    } catch (error) {
      console.error('Error updating company:', error);
      toast.error('Failed to update company details');
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    if (company) setFormData(buildEditFormFromCompany(company));
  };

  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange(isOpen);
    if (!isOpen) setIsEditing(false);
  };



  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-6xl h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Company Details</DialogTitle>
          <DialogDescription>
            View all information for {company?.name}
          </DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="details" className="w-full flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-3 flex-shrink-0">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="integration">Integration</TabsTrigger>
            <TabsTrigger value="bank-details">Bank Details</TabsTrigger>
          </TabsList>
          
          <DetailsTabContent
            company={company}
            isEditing={isEditing}
            formData={formData}
            onFormDataChange={setFormData}
            draggingLogo={draggingLogo}
            onLogoDragOver={handleLogoDragOver}
            onLogoDragLeave={handleLogoDragLeave}
            onLogoDrop={handleLogoDrop}
            onLogoFile={handleLogoFile}
            currencies={currencies}
          />

          <TabsContent value="integration" className="space-y-6 mt-6 flex-1 overflow-y-auto">
            {isEditing && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Integration settings cannot be changed after a company has been created. To update the integration, please contact your administrator.
                </AlertDescription>
              </Alert>
            )}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Integration Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Integration Status</Label>
                  <div>
                    <Badge
                      variant={getIntegrationBadgeVariant(company?.integrationStatus)}
                      className={company?.integrationStatus === 'none' && company?.linkedEvoSubscriptionId ? 'bg-orange-500/10 text-orange-500' : ''}
                    >
                      {company?.integrationStatus === 'connected' && '✓ Connected'}
                      {company?.integrationStatus === 'partial' && '⚠ Partial'}
                      {company?.integrationStatus === 'none' && company?.linkedEvoSubscriptionId && '⏳ Pending Setup'}
                      {company?.integrationStatus === 'none' && !company?.linkedEvoSubscriptionId && 'Not Connected'}
                    </Badge>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Product</Label>
                  <p className="text-sm font-medium">{company?.linkedProductName || '-'}</p>
                </div>
                {company?.linkedProductName && (
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Instance</Label>
                    <p className="text-sm font-medium">{company?.linkedSubscriptionName || '-'}</p>
                  </div>
                )}
                {company?.linkedEvoSubscriptionId && (
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">Sub Ledger</Label>
                    <p className="text-sm font-medium">{company?.linkedSubLedger || '-'}</p>
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Subscription ID</Label>
                  <p className="text-sm font-medium break-all">{company?.linkedEvoSubscriptionId || '-'}</p>
                </div>
              </div>
            </div>
          </TabsContent>

          <BankDetailsTabContent
            isEditing={isEditing}
            bankDetails={localBankDetails}
            onAddRow={handleAddBankRow}
            onRemoveRow={handleRemoveBankRow}
            onChange={handleBankChange}
          />
        </Tabs>
        <DialogFooter className="mt-6 flex-shrink-0">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Button onClick={handleSave}>
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CompanySettingsPage() {
  const navigate = useNavigate();
  const { apiGet, apiPost, apiPut, apiDelete } = useApi();
  const queryClient = useQueryClient();
  void queryClient; // Available for future cache invalidation
  const { isDemo } = useMode();
  const { refreshExternalCompanies } = useCompany();

  // Fetch companies using mode-aware query (demo mode uses mock data, live mode uses API)
  const { data: fetchedCompanies, refetch: refetchCompanies } = useCompaniesList();

  // Local state for companies (initialized from fetched data, can be modified locally)
  const [companies, setCompanies] = useState<Company[]>([]);
  // Tracks whether the manage-integrations dialog was opened as part of a new company creation
  const [isNewCompanyFlow, setIsNewCompanyFlow] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'pending_setup'>('all');
  const [sortOption, setSortOption] = useState<'name-asc' | 'name-desc' | 'newest' | 'oldest'>('name-asc');

  // Update local companies state when fetched data changes
  useEffect(() => {
    if (fetchedCompanies) {
      // Map CompanySettingsUI to local Company type
      const mappedCompanies: Company[] = fetchedCompanies.map(c => ({
        id: c.id,
        name: c.name,
        status: c.status,
        logo: c.logo,
        description: c.description,
        integrationStatus: c.integrationStatus,
        userCount: c.userCount,
        createdAt: c.createdAt,
        linkedEvoSubscriptionId: c.linkedEvoSubscriptionId,
        linkedSubscriptionName: c.linkedSubscriptionName,
        linkedProductName: c.linkedProductName,
        linkedProductId: c.linkedProductId,
        currencyId: c.currencyId,
        code: c.code,
        taxId: c.taxId,
        linkedSubLedger: c.linkedSubLedger,
        externalCompanyName: c.externalCompanyName,
        address: c.address,
        vatRegistrationNumber: c.vatRegistrationNumber,
        companyRegistrationNumber: c.companyRegistrationNumber,
        phone: c.phone,
        email: c.email,
      }));
      setCompanies(mappedCompanies);
    }
  }, [fetchedCompanies]);
  
  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isIntegrationDialogOpen, setIsIntegrationDialogOpen] = useState(false);
  const [isManageIntegrationsDialogOpen, setIsManageIntegrationsDialogOpen] = useState(false);
  const [isManageBankDetailsDialogOpen, setIsManageBankDetailsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewCompanyData, setViewCompanyData] = useState<Company | null>(null);
  
  // Form states
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [formData, setFormData] = useState({
    companyId: '',
    name: '',
    address: '',
    vatRegistrationNo: '',
    companyRegistrationNo: '',
    phone: '',
    email: '',
    status: 'active' as 'active' | 'inactive',
    logo: '',
    description: '',
    currencyId: undefined as number | undefined,
    code: '',
    taxId: '',
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [newCompanyName, _setNewCompanyName] = useState('');
  
  // Logo upload states
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);


        

  // Instances state
  const [instances, setInstances] = useState<Instance[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [selectedInstanceId, setSelectedInstanceId] = useState<string>('');
  const [isLoadingInstances, setIsLoadingInstances] = useState(false);
  const [instancesError, setInstancesError] = useState<string | null>(null);

  // Sub Ledgers state
  const [subLedgers, setSubLedgers] = useState<SubLedgerOption[]>([]);
  const [selectedSubLedger, setSelectedSubLedger] = useState<string>('');
  const [isLoadingSubLedgers, setIsLoadingSubLedgers] = useState(false);
  const [subLedgersError, setSubLedgersError] = useState<string | null>(null);
  const [generatePerSubLedger, setGeneratePerSubLedger] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false); // Track if we're in "add new company" mode

  // Bank Details state
  const [bankDetails, setBankDetails] = useState<BankDetail[]>([
    { id: '1', currency: '', sortCode: '', accountNumber: '', accountName: '', ibanNumber: '', swiftBicCode: '' }
  ]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalCompanies = companies.length;
    const activeIntegrations = companies.filter(c => c.integrationStatus === 'connected').length;
    const activeUsers = companies.reduce((sum, c) => sum + c.userCount, 0);
    const documentTemplates = 15; // Mock value

    return {
      totalCompanies: { value: totalCompanies, trend: 12 },
      activeIntegrations: { value: activeIntegrations, trend: 8 },
      activeUsers: { value: activeUsers, trend: -3 },
      documentTemplates: { value: documentTemplates, trend: 5 },
    };
  }, [companies]);

  // Filter and sort companies
  const filteredAndSortedCompanies = useMemo(() => {
    let filtered = companies;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(company =>
        company.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(company => company.status === statusFilter);
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => sortCompanies(a, b, sortOption));

    return sorted;
  }, [companies, searchTerm, statusFilter, sortOption]);

  // Logo drag & drop handlers
  const handleLogoDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(true);
  }, []);

  const handleLogoDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(false);
  }, []);

  const handleLogoDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleLogoFileSelection = useCallback((file: File) => {
    const error = validateLogoFile(file);
    if (error) {
      toast.error(error);
      return;
    }

    setLogoFile(file);
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleLogoDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleLogoFileSelection(files[0]);
    }
  }, [handleLogoFileSelection]);

  const handleLogoInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleLogoFileSelection(file);
  };

  const removeLogoFile = () => {
    setLogoFile(null);
    setLogoPreview('');
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  const validateCompanyId = (value: string): boolean => {
    // Only allow alphanumeric characters, max 7 characters
    const regex = /^[a-zA-Z0-9]{0,7}$/;
    return regex.test(value);
  };

  const handleAddCompany = () => {
    if (!formData.companyId.trim()) {
      toast.error('Company ID is required');
      return;
    }
    if (!formData.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    // Don't create the company yet — move to integration step first
    const pendingCompany: Company = {
      id: '',
      name: formData.name,
      status: formData.status,
      logo: logoPreview || undefined,
      description: formData.description || undefined,
      integrationStatus: 'none',
      userCount: 0,
      createdAt: new Date().toISOString(),
      code: formData.companyId,
      currencyId: formData.currencyId,
      taxId: formData.taxId,
    };
    setIsAddDialogOpen(false);
    setLogoFile(null);
    setLogoPreview('');
    setSelectedCompany(pendingCompany);
    setSelectedProduct('');
    setSelectedInstanceId('');
    setInstances([]);
    setIsNewCompanyFlow(true);
    setIsManageIntegrationsDialogOpen(true);
  };

  const handleEditCompany = () => {
    if (!selectedCompany) return;
    editCompanyAction(
      selectedCompany, formData, isDemo, apiPut, refetchCompanies, setCompanies,
      () => {
        setIsEditDialogOpen(false);
        setSelectedCompany(null);
        setFormData(INITIAL_FORM_DATA);
      },
    );
  };

  const handleDeleteCompany = () => {
    if (!selectedCompany) return;
    deleteCompanyAction(
      selectedCompany, isDemo, apiDelete, refetchCompanies, setCompanies,
      () => {
        setIsDeleteDialogOpen(false);
        setSelectedCompany(null);
      },
    );
  };

  const openDeleteDialog= (company: Company) => {
    setSelectedCompany(company);
    setIsDeleteDialogOpen(true);
  };

  const handleViewCompany = (company: Company) => {
    setViewCompanyData(company);
    setIsViewDialogOpen(true);
  };

  const handleManageIntegrations = (company: Company) => {
    setSelectedCompany(company);
    setSelectedProduct(company.linkedProductName || '');
    setSelectedInstanceId(company.linkedEvoSubscriptionId || '');
    setInstances([]);
    setSubLedgers([]);
    setSelectedSubLedger('');
    setGeneratePerSubLedger(false);
    setIsManageIntegrationsDialogOpen(true);
  };
  const handleViewDialogSave = (companyId: string, updates: Partial<Company>) => {
    setCompanies(prev => prev.map(c => c.id === companyId ? { ...c, ...updates } : c));
    setViewCompanyData(prev => prev ? { ...prev, ...updates } : prev);
  };


  useEffect(() => {
    fetchInstancesForProduct(selectedProduct, isDemo, apiGet, {
      setInstances,
      setInstanceId: setSelectedInstanceId,
      setLoading: setIsLoadingInstances,
      setError: setInstancesError,
    });
  }, [selectedProduct, apiGet, isDemo]);

  useEffect(() => {
    fetchSubLedgersForInstance(selectedInstanceId, selectedProduct, isDemo, apiGet, {
      setSubLedgers,
      setLoading: setIsLoadingSubLedgers,
      setError: setSubLedgersError,
    });
  }, [selectedInstanceId, selectedProduct, apiGet, isDemo]);



        const handleNextToIntegration = () => {
    // Close Manage Integrations dialog
    setIsManageIntegrationsDialogOpen(false);
    
    // If in add mode, open Add Company dialog; otherwise open Bank Details dialog
    if (isAddMode) {
      setIsAddDialogOpen(true);
    } else {
      setIsManageBankDetailsDialogOpen(true);
    }
  };

  const resetIntegrationState = () => {
    if (isNewCompanyFlow) {
      setIsNewCompanyFlow(false);
      void refreshExternalCompanies();
    }
    setIsManageBankDetailsDialogOpen(false);
    setSelectedCompany(null);
    setSelectedProduct('');
    setSelectedInstanceId('');
    setSubLedgers([]);
    setSelectedSubLedger('');
    setGeneratePerSubLedger(false);
    setBankDetails([INITIAL_BANK_DETAIL]);
    setIsAddMode(false);
  };

  const handleSaveIntegration = async () => {
    if (!selectedCompany) return;
    if (isNewCompanyFlow) {
      // Create company with integration linking in one API call
      const selectedInstance = instances.find(i => i.instanceId === selectedInstanceId);
      const linkedDatabaseName = selectedInstance?.name || undefined;
      const productId = getProductId(selectedProduct);
      try {
        if (isDemo) {
          await new Promise(resolve => setTimeout(resolve, 300));
          const hasSubLedgerDemo = selectedSubLedger !== '' && selectedSubLedger !== SUBLEDGER_NONE_VALUE;
          const mockNewCompany: Company = {
            id: String(Date.now()),
            name: selectedCompany.name,
            status: selectedCompany.status,
            logo: selectedCompany.logo,
            description: selectedCompany.description,
            integrationStatus: selectedInstanceId ? 'connected' : 'none',
            userCount: 0,
            createdAt: new Date().toISOString(),
            code: selectedCompany.code,
            currencyId: selectedCompany.currencyId,
            taxId: selectedCompany.taxId,
            linkedEvoSubscriptionId: selectedInstanceId || undefined,
            linkedSubscriptionName: linkedDatabaseName,
            linkedProductId: productId,
            linkedProductName: selectedProduct || undefined,
            linkedSubLedger: hasSubLedgerDemo ? selectedSubLedger : undefined,
          };
          setCompanies(prev => [...prev, mockNewCompany]);
        } else {
          const hasSubLedger = selectedSubLedger !== '' && selectedSubLedger !== SUBLEDGER_NONE_VALUE;
          await apiPost<CompanyApi>('/api/v1/company', {
            name: selectedCompany.name,
            code: selectedCompany.code,
            currencyId: selectedCompany.currencyId,
            taxId: selectedCompany.taxId,
            linkedSubscriptionId: selectedInstanceId || null,
            linkedSubscriptionName: linkedDatabaseName || null,
            linkedProductId: selectedInstanceId ? productId : null,
            linkedProductName: selectedInstanceId ? selectedProduct : null,
            externalCompanyCode: hasSubLedger ? selectedSubLedger : null,
            externalCompanyName: hasSubLedger ? (subLedgers.find(x => x.code === selectedSubLedger)?.name || selectedSubLedger) : null,
            isMultiCompany: generatePerSubLedger,
          } satisfies CreateCompanyRequest);
          await refetchCompanies();
        }
        toast.success('Company created successfully');
        setFormData(INITIAL_FORM_DATA);
        resetIntegrationState();
      } catch (error) {
        console.error('Error creating company:', error);
        toast.error('Failed to create company');
      }
    } else {
      saveIntegrationAction({
        selectedCompany, selectedInstanceId, selectedProduct, selectedSubLedger, subLedgers,
        instances, isDemo, apiPut, refetchCompanies, updateLocal: setCompanies, onDone: resetIntegrationState,
      });
    }
  };


  const handleCancelIntegration = () => {
    if (isNewCompanyFlow) {
      setIsNewCompanyFlow(false);
      setFormData(INITIAL_FORM_DATA);
      toast.info('Company creation cancelled');
    }
    setIsManageIntegrationsDialogOpen(false);
    setSelectedCompany(null);
    setSelectedProduct('');
    setSelectedInstanceId('');
    setSubLedgers([]);
    setSelectedSubLedger('');
    setGeneratePerSubLedger(false);
    setIsAddMode(false);
    setFormData(INITIAL_FORM_DATA);
    setLogoFile(null);
    setLogoPreview('');
  };

  // Bank Details handlers
  const handleAddBankDetailRow = () => {
    const newId = String(Date.now());
    setBankDetails([...bankDetails, { 
      id: newId, 
      currency: '', 
      sortCode: '', 
      accountNumber: '', 
      accountName: '', 
      ibanNumber: '', 
      swiftBicCode: '' 
    }]);
  };

  const handleRemoveBankDetailRow = (id: string) => {
    if (bankDetails.length > 1) {
      setBankDetails(bankDetails.filter(detail => detail.id !== id));
    }
  };

  const handleBankDetailChange = (id: string, field: keyof BankDetail, value: string) => {
    setBankDetails(bankDetails.map(detail => 
      detail.id === id ? { ...detail, [field]: value } : detail
    ));
  };

  const handleSyncCompany = (company: Company) => syncCompanyAction(company, isDemo, apiPost);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="flex-1 space-y-6 p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">Company Settings</h1>
          </div>
          <p className="text-muted-foreground mt-1">
            Manage your companies, integrations, and organisational settings
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => {
            setIsAddMode(true);
            setIsManageIntegrationsDialogOpen(true);
          }}>
            <Plus className="h-4 w-4 mr-2" />
            Add Company
          </Button>
          <HelpSupportButton screenId="settings-company" />
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="transition-shadow hover:shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Companies</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalCompanies.value}</div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              <TrendIndicator trend={stats.totalCompanies.trend} />
              <span className="ml-1">from last month</span>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer transition-shadow hover:shadow-md"
          onClick={() => navigate('/settings/integrations')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Integrations</CardTitle>
            <Plug className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeIntegrations.value}</div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              <TrendIndicator trend={stats.activeIntegrations.trend} />
              <span className="ml-1">from last month</span>
            </div>
          </CardContent>
        </Card>

        <Card className="transition-shadow hover:shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Users</CardTitle>
            <UsersIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeUsers.value}</div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              <TrendIndicator trend={stats.activeUsers.trend} />
              <span className="ml-1">from last month</span>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/settings/templates')}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Document Templates</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.documentTemplates.value}</div>
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              <TrendIndicator trend={stats.documentTemplates.trend} />
              <span className="ml-1">from last month</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Companies</CardTitle>
          <CardDescription>Manage your company profiles and settings</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={(value: 'all' | 'active' | 'inactive' | 'pending_setup') => setStatusFilter(value)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="pending_setup">Pending Setup</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortOption} onValueChange={(value: 'name-asc' | 'name-desc' | 'newest' | 'oldest') => setSortOption(value)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name-asc">Name A-Z</SelectItem>
                <SelectItem value="name-desc">Name Z-A</SelectItem>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="oldest">Oldest</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Companies Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company ID</TableHead>
                  <TableHead>Company Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead>Integration</TableHead>
                  <TableHead>Linked Database</TableHead>
                  <TableHead>Linked Product</TableHead>
                  <TableHead>Linked Sub Ledger</TableHead>
                  <TableHead>Users</TableHead>
                  <TableHead>Created Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSortedCompanies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center text-muted-foreground py-8">
                      No companies found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAndSortedCompanies.map((company) => (
                    <TableRow 
                      key={company.id}
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleViewCompany(company)}
                    >
                      <TableCell>
                        {company.code || '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={company.logo} />
                            <AvatarFallback>{company.name.substring(0, 2).toUpperCase()}</AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium">{company.name}</div>
                            {company.description && (
                              <div className="text-sm text-muted-foreground">{company.description}</div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {company.status === 'active' && (
                          <Badge variant="default">Active</Badge>
                        )}
                        {company.status === 'pending_setup' && (
                          <Badge className="bg-orange-500/10 text-orange-500 hover:bg-orange-500/20">
                            Pending Setup
                          </Badge>
                        )}
                        {company.status !== 'active' && company.status !== 'pending_setup' && (
                          <Badge variant="secondary">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {company.currencyId || '-'}
                      </TableCell>
                      <TableCell>
                        <IntegrationStatusBadge
                          status={company.integrationStatus}
                          onManageClick={(e) => { e.stopPropagation(); handleManageIntegrations(company); }}
                          company={company}
                        />
                      </TableCell>
                      <TableCell>{company.linkedSubscriptionName || '-'}</TableCell>
                      <TableCell>{company.linkedProductName || '-'}</TableCell>
                      <TableCell>{company.linkedSubLedger || '-'}</TableCell>
                      <TableCell>{company.userCount}</TableCell>
                      <TableCell>{formatDate(company.createdAt)}</TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewCompany(company)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View
                            </DropdownMenuItem>
                            {company.integrationStatus !== 'none' && (
                              <DropdownMenuItem onClick={() => handleSyncCompany(company)}>
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Sync
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => openDeleteDialog(company)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add Company Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Company</DialogTitle>
            <DialogDescription>
              Create a new company profile. You can set up integrations after creation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="companyId">
                Company ID <span className="text-destructive">*</span>
              </Label>
              <Input
                id="companyId"
                placeholder="Enter company ID (max 7 alphanumeric characters)"
                value={formData.companyId}
                onChange={(e) => {
                  const value = e.target.value;
                  if (validateCompanyId(value)) {
                    setFormData({ ...formData, companyId: value });
                  }
                }}
                maxLength={7}
              />
              <p className="text-xs text-muted-foreground">
                Alphanumeric characters only, maximum 7 characters
              </p>
            </div>
            <div className="grid grid-cols-[2fr_1fr] gap-6">
              <div className="space-y-2">
                <Label htmlFor="name">
                  Company Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  placeholder="Enter company name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency">Currency</Label>
                <Input
                  id="currency"
                  placeholder="Will be imported from integration"
                  value={formData.currencyId || ''}
                  disabled
                  className="bg-muted cursor-not-allowed"
                />
                <p className="text-xs text-muted-foreground">
                  Imported when integration is enabled
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                placeholder="Enter company address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="vatRegistrationNo">VAT Registration No.</Label>
                <Input
                  id="vatRegistrationNo"
                  placeholder="Enter VAT registration number"
                  value={formData.vatRegistrationNo}
                  onChange={(e) => setFormData({ ...formData, vatRegistrationNo: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="companyRegistrationNo">Company Registration No.</Label>
                <Input
                  id="companyRegistrationNo"
                  placeholder="Enter company registration number"
                  value={formData.companyRegistrationNo}
                  onChange={(e) => setFormData({ ...formData, companyRegistrationNo: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  placeholder="Enter phone number"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter email address"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="status">Active Status</Label>
              <Switch
                id="status"
                checked={formData.status === 'active'}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, status: checked ? 'active' : 'inactive' })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="logo">Company Logo</Label>
              {!logoFile ? (
                <div
                  onDragEnter={handleLogoDragEnter}
                  onDragOver={handleLogoDragOver}
                  onDragLeave={handleLogoDragLeave}
                  onDrop={handleLogoDrop}
                  className={cn(
                    "border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer",
                    isDraggingLogo
                      ? "border-primary bg-primary/5"
                      : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50"
                  )}
                  onClick={() => logoInputRef.current?.click()}
                >
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/svg+xml"
                    onChange={handleLogoInputChange}
                    className="hidden"
                  />
                  <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-sm font-medium mb-1">
                    {isDraggingLogo ? "Drop logo here" : "Drag and drop logo here"}
                  </p>
                  <p className="text-xs text-muted-foreground mb-2">
                    or click to browse your computer
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Supported formats: PNG, JPG, GIF, SVG (Max 5MB)
                  </p>
                </div>
              ) : (
                <div className="border rounded-lg p-4 bg-muted/50">
                  <div className="flex items-center gap-4">
                    <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-background border">
                      {logoPreview ? (
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="h-8 w-8 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{logoFile.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {(logoFile.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={removeLogoFile}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsAddDialogOpen(false);
              setIsAddMode(false);
              setLogoFile(null);
              setLogoPreview('');
              setFormData(INITIAL_FORM_DATA);
              setSelectedProduct('');
              setSelectedInstanceId('');
              setSelectedSubLedger('');
              setGeneratePerSubLedger(false);
            }}>
              Cancel
            </Button>
            <Button onClick={() => {
              if (isAddMode) {
                // In add mode, create pending company and move to Bank Details dialog
                const pendingCompany: Company = {
                  id: '',
                  name: formData.name,
                  status: formData.status,
                  logo: logoPreview || undefined,
                  description: formData.description || undefined,
                  integrationStatus: 'none',
                  userCount: 0,
                  createdAt: new Date().toISOString(),
                  code: formData.companyId,
                  currencyId: formData.currencyId,
                  taxId: formData.taxId,
                };
                setSelectedCompany(pendingCompany);
                setIsNewCompanyFlow(true);
                setIsAddDialogOpen(false);
                setLogoFile(null);
                setLogoPreview('');
                setIsManageBankDetailsDialogOpen(true);
              } else {
                // In edit mode (existing flow), save the company
                handleAddCompany();
              }
            }} disabled={!formData.companyId || !formData.name}>
              Next
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Company</DialogTitle>
            <DialogDescription>
              Update company information and settings.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Company Name *</Label>
              <Input
                id="edit-name"
                placeholder="Enter company name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                placeholder="Enter company description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="edit-status">Active Status</Label>
              <Switch
                id="edit-status"
                checked={formData.status === 'active'}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, status: checked ? 'active' : 'inactive' })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-logo">Logo URL</Label>
              <Input
                id="edit-logo"
                placeholder="Enter logo URL (optional)"
                value={formData.logo}
                onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditCompany} disabled={!formData.name}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{selectedCompany?.name}</strong> and all associated data.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCompany} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Integration Setup Dialog */}
      <Dialog open={isIntegrationDialogOpen} onOpenChange={setIsIntegrationDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Up Integrations</DialogTitle>
            <DialogDescription>
              Would you like to set up integrations for <strong>{newCompanyName}</strong> now?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsIntegrationDialogOpen(false)}>
              Skip for Now
            </Button>
            <Button onClick={() => {
              setIsIntegrationDialogOpen(false);
              navigate('/settings/integrations');
            }}>
              Yes, Set Up Integrations
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Integrations Dialog */}
      <Dialog open={isManageIntegrationsDialogOpen} onOpenChange={setIsManageIntegrationsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Integrations</DialogTitle>
            <DialogDescription>
              {isAddMode 
                ? "Select a product and instance to link to your new company."
                : `Link ${selectedCompany?.name} to an instance.`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="product">Product</Label>
              <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Financials">Financials</SelectItem>
                  <SelectItem value="DimensionsvNext">DimensionsvNext</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {selectedProduct && (
              <div className="space-y-2">
                <Label htmlFor="instance">Instance</Label>
                {isLoadingInstances ? (
                  <div className="text-sm text-muted-foreground">Loading instances...</div>
                ) : instancesError ? (
                  <div className="text-sm text-destructive">{instancesError}</div>
                ) : (
                  <Select value={selectedInstanceId} onValueChange={setSelectedInstanceId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select an instance" />
                    </SelectTrigger>
                    <SelectContent>
                      {instances.map((instance) => (
                        <SelectItem key={instance.instanceId} value={instance.instanceId}>
                          {instance.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
            {selectedInstanceId && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="generatePerSubLedger"
                  checked={generatePerSubLedger}
                  onCheckedChange={(checked) => {
                    setGeneratePerSubLedger(checked as boolean);
                    if (!checked) {
                      setSelectedSubLedger('');
                    }
                  }}
                />
                <Label
                  htmlFor="generatePerSubLedger"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Generate Companies per Sub Ledger
                </Label>
              </div>
            )}
            {selectedInstanceId && generatePerSubLedger && (
              <div className="space-y-2">
                <Label htmlFor="subLedger">Sub Ledger</Label>
                {isLoadingSubLedgers ? (
                  <div className="text-sm text-muted-foreground">Loading sub ledgers...</div>
                ) : subLedgersError ? (
                  <div className="text-sm text-destructive">{subLedgersError}</div>
                ) : (
                  <Select value={selectedSubLedger || SUBLEDGER_NONE_VALUE} onValueChange={(val) => setSelectedSubLedger(val === SUBLEDGER_NONE_VALUE ? '' : val)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a sub ledger" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SUBLEDGER_NONE_VALUE}>— None —</SelectItem>
                      {subLedgers.map((subLedger) => (
                        <SelectItem key={subLedger.code} value={subLedger.code}>
                          {subLedger.code} - {subLedger.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="flex justify-between sm:justify-between">
            <div />
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancelIntegration}>
                Skip for Now
              </Button>
              <Button onClick={handleNextToIntegration} disabled={isLoadingInstances}>
                Next
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Manage Bank Details Dialog */}
      <Dialog open={isManageBankDetailsDialogOpen} onOpenChange={setIsManageBankDetailsDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Bank Details</DialogTitle>
            <DialogDescription>
              {isAddMode 
                ? `Add bank account details for ${formData.name}.`
                : `Add bank account details for ${selectedCompany?.name}.`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[120px]">Currency</TableHead>
                    <TableHead className="w-[120px]">Sort Code</TableHead>
                    <TableHead className="w-[150px]">Account Number</TableHead>
                    <TableHead className="w-[180px]">Account Name</TableHead>
                    <TableHead className="w-[180px]">IBAN Number</TableHead>
                    <TableHead className="w-[150px]">SWIFT/BIC Code</TableHead>
                    <TableHead className="w-[80px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bankDetails.map((detail) => (
                    <TableRow key={detail.id}>
                      <TableCell>
                        <Input
                          placeholder="USD"
                          value={detail.currency}
                          onChange={(e) => handleBankDetailChange(detail.id, 'currency', e.target.value)}
                          className="h-9"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          placeholder="12-34-56"
                          value={detail.sortCode}
                          onChange={(e) => handleBankDetailChange(detail.id, 'sortCode', e.target.value)}
                          className="h-9"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          placeholder="12345678"
                          value={detail.accountNumber}
                          onChange={(e) => handleBankDetailChange(detail.id, 'accountNumber', e.target.value)}
                          className="h-9"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          placeholder="Business Account"
                          value={detail.accountName}
                          onChange={(e) => handleBankDetailChange(detail.id, 'accountName', e.target.value)}
                          className="h-9"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          placeholder="GB29NWBK60161331926819"
                          value={detail.ibanNumber}
                          onChange={(e) => handleBankDetailChange(detail.id, 'ibanNumber', e.target.value)}
                          className="h-9"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          placeholder="NWBKGB2L"
                          value={detail.swiftBicCode}
                          onChange={(e) => handleBankDetailChange(detail.id, 'swiftBicCode', e.target.value)}
                          className="h-9"
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveBankDetailRow(detail.id)}
                          disabled={bankDetails.length === 1}
                          className="h-9 w-9"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="mt-4">
              <Button variant="outline" onClick={handleAddBankDetailRow} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Add Bank Detail Row
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              if (isNewCompanyFlow) {
                setIsNewCompanyFlow(false);
                setFormData(INITIAL_FORM_DATA);
                toast.info('Company creation cancelled');
              }
              setIsManageBankDetailsDialogOpen(false);
              setSelectedCompany(null);
              setSelectedProduct('');
              setSelectedInstanceId('');
              setSubLedgers([]);
              setSelectedSubLedger('');
              setBankDetails([{ id: '1', currency: '', sortCode: '', accountNumber: '', accountName: '', ibanNumber: '', swiftBicCode: '' }]);
            }}>
              Cancel
            </Button>
            <Button onClick={handleSaveIntegration}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CompanyViewDialog
        open={isViewDialogOpen}
        onOpenChange={setIsViewDialogOpen}
        company={viewCompanyData}
        isDemo={isDemo}
        apiGet={apiGet}
        apiPut={apiPut}
        refetchCompanies={refetchCompanies}
        onSave={handleViewDialogSave}
      />
    </div>
  );
}
